import Divider from "@mui/material/Divider";
import { Suspense, useEffect, useMemo } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAccounts } from "services/account/useAccounts";
import { useNetworkPolicies } from "services/networkPolicy/useNetworkPolicies";
import {
  EMPTY_NETWORK_POLICY,
  ServiceAccount,
} from "services/serviceAccounts/types";

import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import FormRow from "components/FormRow";
import formRowStyles from "components/FormRow/FormRow.module.scss";
import { InputDisabledContext } from "components/InputState/InputDisabledContext";
import LoadingOverlap from "components/LoadingOverlap";
import { Modal } from "components/Modal/Modal";
import OutlinedSelect from "components/OutlinedSelect";
import Spinner from "components/Spinner";
import { TextInput } from "components/TextInput/TextInput";
import Toggle from "components/Toggle";

import { AssociateUser } from "./AssociateUser";
import { UserSection } from "./UserSection";
import { AssignType, Fields, ServiceAccountFormFields } from "./schema";

import styles from "./styles.module.scss";

type Props = {
  title: string;
  modalAction: string;
  isLoading: boolean;
  onClose: () => void;
  onFormSubmit: (data: Fields) => void;
  form: UseFormReturn<Fields>;
  serviceAccount?: ServiceAccount;
  allowedAccountNames?: string[];
};

export const ServiceAccountFields = (props: {
  form: UseFormReturn<Fields> | UseFormReturn<ServiceAccountFormFields>;
  isLoading: boolean;
}) => {
  const { form, isLoading } = props;
  const {
    register,
    formState: { errors },
    control,
    watch,
  } = form as UseFormReturn<ServiceAccountFormFields>;

  const { t } = useTranslation();
  const networkPolicy = watch("networkPolicyName");

  const networkPolicies = useNetworkPolicies();

  const networkPoliciesOptions = useMemo(() => {
    const sortedNetworkPolicies = [...networkPolicies];
    sortedNetworkPolicies.sort((a, b) =>
      a.networkPolicyName.localeCompare(b.networkPolicyName)
    );

    return [
      <ContextMenuItem
        key={EMPTY_NETWORK_POLICY}
        value={EMPTY_NETWORK_POLICY}
        testId="policy-default"
        text={t("configuration.default")}
        checked={networkPolicy === EMPTY_NETWORK_POLICY}
      />,
      ...sortedNetworkPolicies.map(policy => (
        <ContextMenuItem
          key={policy.networkPolicyName}
          value={policy.networkPolicyName}
          testId={`policy-${policy.networkPolicyName}`}
          checkedIconPlaceholder={true}
          text={policy.networkPolicyName}
          checked={policy.networkPolicyName === networkPolicy}
        />
      )),
    ];
  }, [networkPolicies, networkPolicy, t]);

  return (
    <>
      <FormRow
        required
        label={t("serviceAccounts.modal.name")}
        input={
          <TextInput
            testId="name-input"
            inputRootClassName={formRowStyles.nameInput}
            className={formRowStyles.nameInputContainer}
            {...register("name")}
            error={!!errors?.name}
            helperText={errors?.name?.message}
            required
            autoComplete="off"
            autoFocus
          />
        }
      />

      <FormRow
        label={t("serviceAccounts.modal.description")}
        input={
          <TextInput
            testId="description-input"
            inputRootClassName={styles.description}
            className={formRowStyles.nameInputContainer}
            {...register("description")}
            error={!!errors?.description}
            helperText={errors?.description?.message}
            autoComplete="off"
            multiline
          />
        }
      />

      <FormRow
        label={t("serviceAccounts.modal.network_policy")}
        input={
          <Controller
            control={control}
            name="networkPolicyName"
            render={({ field: { onChange, value } }) => (
              <OutlinedSelect
                searchOptions={{
                  searchPlaceholder: t(
                    "serviceAccounts.filters.search_policies"
                  ),
                  noResultsText: t(
                    "serviceAccounts.filters.no_policies_found"
                  ) as string,
                }}
                allowUncheck={false}
                className={formRowStyles.selectInput}
                testId="network-policy-select"
                controlledValue={value ? [value] : []}
                renderValue={items => {
                  const [value] = items;
                  if (value === EMPTY_NETWORK_POLICY) {
                    return "Default";
                  }
                  return value;
                }}
                onSelect={(items: string[]) => {
                  if (!items.length) {
                    return;
                  }
                  const [policy] = items;
                  onChange(policy);
                }}
              >
                {networkPoliciesOptions}
              </OutlinedSelect>
            )}
          />
        }
      />
      <Controller
        control={control}
        name="isAdmin"
        render={({ field: { onChange, value } }) => (
          <FormRow
            label={
              <div className={styles.label}>
                {t("serviceAccounts.modal.is_admin")}
                <div className={styles.label__help}>
                  {t("serviceAccounts.modal.is_admin_description")}
                </div>
              </div>
            }
            classes={{ inputContainer: styles.toggle }}
            input={
              <Toggle
                disabled={isLoading}
                dataTestId="is-org-admin-field"
                checked={value}
                onChange={checked => {
                  onChange(checked);
                }}
                size="sm"
                tightRight
              />
            }
          />
        )}
      />
    </>
  );
};

export const ServiceAccountModal = (props: Props) => {
  const {
    title,
    modalAction,
    form,
    isLoading,
    onClose,
    onFormSubmit,
    serviceAccount,
    allowedAccountNames,
  } = props;

  const {
    handleSubmit,
    formState: { isDirty },
    setValue,
    control,
    watch,
  } = form;

  const { t } = useTranslation();
  const accounts = useAccounts();
  const accountName = watch("accountName");
  const assignType = watch("assignType");

  const filteredAccounts = useMemo(() => {
    if (!allowedAccountNames) {
      return accounts;
    }

    return accounts.filter(account =>
      allowedAccountNames.includes(account.accountName)
    );
  }, [accounts, allowedAccountNames]);

  useEffect(() => {
    if (!allowedAccountNames || !allowedAccountNames.length) {
      return;
    }

    if (!allowedAccountNames.includes(accountName)) {
      setValue("accountName", allowedAccountNames[0]);
    }
  }, [allowedAccountNames, accountName, setValue]);

  return (
    <InputDisabledContext.Provider value={isLoading}>
      <Modal
        title={title}
        onClose={onClose}
        primaryButton={{
          text: modalAction,
          onSubmit: handleSubmit(onFormSubmit),
          isLoading,
          disabled: !isDirty || isLoading,
        }}
      >
        <Suspense fallback={<LoadingOverlap isLoading />}>
          <ServiceAccountFields
            form={form}
            isLoading={isLoading}
          />
          <Divider className={styles.divider} />

          <FormRow
            label={t("serviceAccounts.modal.select_account")}
            input={
              <Controller
                control={control}
                name="accountName"
                render={({ field: { onChange, value } }) => (
                  <OutlinedSelect
                    allowUncheck={false}
                    className={formRowStyles.selectInput}
                    testId="account-select"
                    controlledValue={[accountName]}
                    disabled={filteredAccounts.length <= 1}
                    onSelect={(items: string[]) => {
                      if (!items.length) {
                        return;
                      }
                      const [type] = items;
                      setValue("assignType", AssignType.assign);
                      onChange(type);
                    }}
                  >
                    {filteredAccounts.map(account => {
                      const { accountName } = account;
                      return (
                        <ContextMenuItem
                          value={accountName}
                          key={accountName}
                          testId={`account-${accountName}`}
                          checked={accountName === value}
                          checkedIconPlaceholder={true}
                          text={accountName}
                        />
                      );
                    })}
                  </OutlinedSelect>
                )}
              />
            }
          />
          <Suspense
            fallback={
              <FormRow
                label={t("serviceAccounts.modal.associate_user")}
                required={!serviceAccount}
                input={
                  <OutlinedSelect
                    disabled
                    prefix={<Spinner size={12} />}
                  />
                }
              />
            }
          >
            <AssociateUser
              form={form}
              serviceAccount={serviceAccount}
            />
            {assignType === "create" && <UserSection form={form} />}
          </Suspense>
        </Suspense>
      </Modal>
    </InputDisabledContext.Provider>
  );
};
